/**
 * Quick Pair Matching Service
 * Finds and ranks potential matches based on distance, rating, and preferences
 */

/**
 * Calculate distance between two coordinates in kilometers
 * Uses Haversine formula
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Calculate match quality score (0-100)
 * Higher score = better match
 */
export const calculateMatchScore = (request, candidate, phase = 1) => {
  let score = 100;

  // Distance penalty (most important factor)
  const distance = calculateDistance(
    request.latitude,
    request.longitude,
    candidate.latitude,
    candidate.longitude
  );
  
  if (phase === 1) {
    // Strict phase: exponential penalty
    score -= distance * 20; // Lose 20 points per km
  } else if (phase === 2) {
    // Loosened phase: linear penalty
    score -= distance * 10;
  } else {
    // Any match phase: minimal penalty
    score -= distance * 5;
  }

  // Rating difference penalty
  const ratingDiff = Math.abs((request.userRating || 1200) - (candidate.user.inPersonRating || 1200));
  
  if (phase === 1) {
    score -= ratingDiff / 10; // Lose 1 point per 10 rating diff
  } else if (phase === 2) {
    score -= ratingDiff / 20;
  } else {
    score -= ratingDiff / 40;
  }

  // Time format match bonus
  if (request.timeFormat === candidate.timeFormat) {
    score += 10;
  } else if (phase === 1) {
    score -= 50; // Large penalty in strict phase
  }

  // Game type match bonus
  if (request.gameType === candidate.gameType) {
    score += 5;
  }

  return Math.max(0, score);
};

/**
 * Find matching candidates for a Quick Pair request
 * @param {object} myRequest - My QuickPairRequest
 * @param {array} allRequests - All active QuickPairRequests from other users
 * @param {number} phase - Search phase (1=strict, 2=loosened, 3=any)
 * @returns {array} Array of candidates with scores, sorted by best match
 */
export const findMatches = (myRequest, allRequests, phase = 1) => {
  console.log(`🔍 findMatches: Phase ${phase}, checking ${allRequests.length} requests`);
  console.log(`   My request: userId=${myRequest.userId?.substring(0, 8)}..., lat=${myRequest.latitude}, lon=${myRequest.longitude}, gameType=${myRequest.gameType}, timeFormat=${myRequest.timeFormat}, rating=${myRequest.userRating || 1200}, maxDistance=${myRequest.maxDistance || 5}`);
  
  const candidates = allRequests.filter((req) => {
    // Don't match with yourself (compare as strings to handle UUID format differences)
    if (String(req.userId) === String(myRequest.userId)) {
      console.log(`   ❌ ${req.user?.username || 'unknown'}: Self-match (userId: ${req.userId?.substring(0, 8)}...)`);
      return false;
    }

    // Must be active
    if (!req.isActive) {
      console.log(`   ❌ ${req.user?.username || 'unknown'}: Not active`);
      return false;
    }

    // Calculate distance
    const distance = calculateDistance(
      myRequest.latitude,
      myRequest.longitude,
      req.latitude,
      req.longitude
    );

    // Apply phase-specific distance filters
    if (phase === 1) {
      if (distance > (myRequest.maxDistance || 5)) {
        console.log(`   ❌ ${req.user?.username || 'unknown'}: Distance too far (${distance.toFixed(2)}km > ${myRequest.maxDistance || 5}km)`);
        return false;
      }
    } else if (phase === 2) {
      if (distance > 8) {
        console.log(`   ❌ ${req.user?.username || 'unknown'}: Distance too far (${distance.toFixed(2)}km > 8km)`);
        return false;
      }
    } else {
      if (distance > 10) {
        console.log(`   ❌ ${req.user?.username || 'unknown'}: Distance too far (${distance.toFixed(2)}km > 10km)`);
        return false;
      }
    }

    // Apply phase-specific rating filters
    if (phase === 1) {
      const ratingDiff = Math.abs(
        (myRequest.userRating || 1200) - (req.user?.inPersonRating || 1200)
      );
      if (ratingDiff > 200) {
        console.log(`   ❌ ${req.user?.username || 'unknown'}: Rating diff too large (${ratingDiff} > 200)`);
        return false;
      }
    } else if (phase === 2) {
      const ratingDiff = Math.abs(
        (myRequest.userRating || 1200) - (req.user?.inPersonRating || 1200)
      );
      if (ratingDiff > 400) {
        console.log(`   ❌ ${req.user?.username || 'unknown'}: Rating diff too large (${ratingDiff} > 400)`);
        return false;
      }
    }

    // Apply phase-specific time format filters
    if (phase === 1) {
      if (myRequest.timeFormat !== req.timeFormat) {
        console.log(`   ❌ ${req.user?.username || 'unknown'}: Time format mismatch (${myRequest.timeFormat} !== ${req.timeFormat})`);
        return false;
      }
    }

    // Apply phase-specific game type filters
    if (phase === 1) {
      if (myRequest.gameType !== req.gameType) {
        console.log(`   ❌ ${req.user?.username || 'unknown'}: Game type mismatch (${myRequest.gameType} !== ${req.gameType})`);
        return false;
      }
    }

    console.log(`   ✅ ${req.user?.username || 'unknown'}: PASSED all filters (distance: ${distance.toFixed(2)}km)`);
    return true;
  });

  // Calculate scores and add distance
  const candidatesWithScores = candidates.map((candidate) => ({
    ...candidate,
    score: calculateMatchScore(myRequest, candidate, phase),
    distance: calculateDistance(
      myRequest.latitude,
      myRequest.longitude,
      candidate.latitude,
      candidate.longitude
    ),
  }));

  // Sort by score (highest first)
  candidatesWithScores.sort((a, b) => b.score - a.score);

  return candidatesWithScores;
};

/**
 * Get match quality label
 */
export const getMatchQualityLabel = (phase) => {
  if (phase === 1) return 'Good Match';
  if (phase === 2) return 'Fair Match';
  return 'Any Match';
};

/**
 * Format distance for display
 */
export const formatDistance = (km) => {
  if (km < 1) {
    return `${Math.round(km * 1000)}m away`;
  }
  return `${km.toFixed(1)}km away`;
};

/**
 * Get adjacent time formats for loosened matching
 */
export const getAdjacentTimeFormats = (format) => {
  const formats = ['BULLET', 'BLITZ', 'RAPID', 'CLASSICAL'];
  const index = formats.indexOf(format);
  
  const adjacent = [format];
  if (index > 0) adjacent.push(formats[index - 1]);
  if (index < formats.length - 1) adjacent.push(formats[index + 1]);
  
  return adjacent;
};

